import * as Sentry from "@sentry/browser"
import { Breadcrumb, SeverityLevel } from "@sentry/vue"

export type GenericLogObject = {
  message: string
  // Error Object
  error?: unknown
  // Extra Data
  data?: Record<string, unknown>
}

class Logger {
  private readonly _isProduction: boolean

  constructor() {
    this._isProduction = Boolean(process.env.PROD)
  }

  public info(message: string) {
    this._isProduction ? Sentry.captureMessage(message) : console.info(message)
  }

  public debug(breadcrumb: Breadcrumb | string) {
    const breadcrumbData = (
      typeof breadcrumb === "string" ? { data: breadcrumb } : breadcrumb
    ) as Breadcrumb

    if (this._isProduction) {
      Sentry.addBreadcrumb(breadcrumbData)
    } else {
      if (typeof breadcrumb === "string") {
        console.debug(breadcrumb)
      } else {
        console.debug(breadcrumb.message ?? "DEBUG", breadcrumb)
      }
    }
  }

  public warning(data: GenericLogObject) {
    this._isProduction ? this.genericSentryCapture("warning", data) : console.warn(data)
  }

  public error(data: GenericLogObject) {
    this._isProduction ? this.genericSentryCapture("error", data) : console.error(data)
  }

  private async genericSentryCapture(level: SeverityLevel, exception: GenericLogObject) {
    const { useConfigurationStore } = await import("src/stores/configurationStore")
    const { useUserStore } = await import("src/stores/userStore")
    const { useAuthStore } = await import("src/stores/authStore")

    const userStore = useUserStore()
    const configStore = useConfigurationStore()
    const authStore = useAuthStore()

    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
    const viewSize = { vw, vh }

    Sentry.withScope((scope) => {
      scope.setLevel(level)
      scope.setTransactionName(exception.message)

      const userObject = {
        ...userStore.getUserData,
        token: authStore.authenticationToken?.access_token.slice(0, 3),
      }

      scope.setUser(userObject)

      scope.setExtra("Screen Size", viewSize)
      scope.setExtra("User Configurations", configStore.getCurrentConfiguration)

      // Add extra data
      if (exception.data) {
        scope.setExtra("extraData", exception.data)
      }

      if (exception.error && exception.error instanceof Error) {
        Sentry.captureException(exception.error)
      } else {
        if (exception.error) scope.setExtra("errorObject", exception.error)
        Sentry.captureException(new Error(exception.message))
      }
    })
  }
}

export const Log = new Logger()
